import { entries, isEmpty } from 'lodash'
import { DeviationAnalysisType, TransformedDeviationSchema, ValuationDeviationOption } from '../DeviationAnalysis/types'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Avatar, Box, ClickAwayListener, Popper, styled } from '@mui/material'
import { isKeyOf, isoDateStringToLocalFormat, LoadingSkeleton, MonetaryValue, tokens, User } from 'ComponentLibrary'
import { DEVIATION_ANALYSIS_LABELS, type DeviationAnalysisRowLabelKey } from '../DeviationAnalysis/constants'
import { mapDeviationAnalysisSchemaToTable } from '../DeviationAnalysis/mapDeviationAnalysisSchemaToTable'
import React, { useState } from 'react'
import { DeviationLabel } from '../DeviationAnalysis/DeviationLabel'
import { DeviationValue } from '../DeviationAnalysis/DeviationValue'
import { DeviationChangePercentage, DeviationChangeValue } from '../DeviationAnalysis/DeviationChange'
import { AnnotationMessage, DeviationAnnotation } from './types'
import { TextBubble, TextBubbleCreation, type TextBubbleCopy } from './TextBubble'
import { UserCircleIcon } from '@phosphor-icons/react'
import type { DeviationAnalysisWidgetCopy } from '../DeviationAnalysis/DeviationAnalysisWidget'
import type { UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type DeviationAnalysisWidgetProps = {
    deviationAnalysis?: DeviationAnalysisType
    isDeviationAnalysisPending?: boolean
    baseValuation?: ValuationDeviationOption
    comparisonValuation?: ValuationDeviationOption
    annotations?: DeviationAnnotation[]
    addAnnotation: (annotation: DeviationAnnotation, onSuccess?: () => void) => void
    addReply: (reply: AnnotationMessage, annotationId?: string, onSuccess?: () => void) => void
    editAnnotation: (updatedText: string, annotationId?: string, onSuccess?: () => void) => void
    editReply: (updatedText: string, annotationId?: string, replyId?: string, onSuccess?: () => void) => void
    deleteAnnotation: (annotationId?: string, cascade?: boolean, onSuccess?: () => void) => void
    deleteReply: (annotationId?: string, replyId?: string, onSuccess?: () => void) => void
    currentUser?: User
    isAddAnnotationPending?: boolean
    isDeleteAnnotationPending?: boolean
    isEditAnnotationPending?: boolean
    isImmediatelyVisible?: boolean
    hasMultipleDrafts: boolean
    showNotificationTooltip?: boolean
    draftId?: string
    hasNewerDraft: boolean
    filterOlderAnnotations: boolean
    copy?: DeviationAnalysisWidgetCopy
    textBubbleCopy?: TextBubbleCopy
    userLinkCopy?: Partial<UserLinkCopy>
    locale?: string
}

const DEFAULT_DEVIATION_ANALYSIS_WIDGET_COPY: DeviationAnalysisWidgetCopy = {
    sectionLabels: {
        marketValueResults: DEVIATION_ANALYSIS_LABELS.marketValueResults,
        keyEarningsFigures: DEVIATION_ANALYSIS_LABELS.keyEarningsFigures,
        costs: DEVIATION_ANALYSIS_LABELS.costs,
        yieldFigures: DEVIATION_ANALYSIS_LABELS.yieldFigures,
        valuationRelevantAreas: DEVIATION_ANALYSIS_LABELS.valuationRelevantAreas,
        valuationStructureLifeSpans: DEVIATION_ANALYSIS_LABELS.valuationStructureLifeSpans,
    },
    rowLabels: { ...DEVIATION_ANALYSIS_LABELS },
    compactComparisonLabel: 'Vorbewertung',
    currentLabel: 'Aktuell',
    changeLabel: 'Veränderung',
}

const AnnotationUserAvatar = ({ user }: { user?: User }) => {
    const [avatarImageSrc, setAvatarImageSrc] = useState(user?.profile_thumbnail_image_url)

    if (!user) {
        return <Box width={tokens.SizingIconS} height={tokens.SizingIconS} />
    }

    return (
        <Avatar
            src={avatarImageSrc}
            onError={() => {
                if (user?.profile_image_url && user.profile_image_url !== avatarImageSrc) {
                    setAvatarImageSrc(user.profile_image_url)
                }
            }}
            style={{ backgroundColor: 'unset', width: tokens.SizingIconS, height: tokens.SizingIconS }}
        >
            <UserCircleIcon weight="bold" color={tokens.ColorIconLinkDefault} size={tokens.SizingIconS} />
        </Avatar>
    )
}

export function AnnotatedDeviationAnalysisWidget({
    deviationAnalysis,
    isDeviationAnalysisPending,
    baseValuation,
    comparisonValuation,
    annotations,
    addAnnotation,
    addReply,
    editAnnotation,
    editReply,
    deleteAnnotation,
    deleteReply,
    currentUser,
    isAddAnnotationPending,
    isEditAnnotationPending,
    isDeleteAnnotationPending,
    isImmediatelyVisible,
    hasMultipleDrafts,
    showNotificationTooltip,
    draftId,
    hasNewerDraft,
    filterOlderAnnotations,
    copy = DEFAULT_DEVIATION_ANALYSIS_WIDGET_COPY,
    textBubbleCopy,
    userLinkCopy,
    locale = 'de-DE',
}: DeviationAnalysisWidgetProps) {
    const transformedDeviationAnalysis = mapDeviationAnalysisSchemaToTable(deviationAnalysis)
    const percentagePointUnit = copy.percentagePointUnit ?? 'BP'
    const yearUnitLabels = copy.yearUnitLabels ?? { one: 'Jahr', other: 'Jahre' }

    const [hoveredRow, setHoveredRow] = useState<string | undefined>()
    const [selectedRow, setSelectedRow] = useState<string | undefined>()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const getDeviationTableRowLabel = (
        key: DeviationAnalysisRowLabelKey,
        value: TransformedDeviationSchema<MonetaryValue> & Partial<{ customLabel?: string }>
    ): string => {
        if (value && 'customLabel' in value && value.customLabel) {
            return value.customLabel
        }
        return copy.rowLabels[key]
    }

    const getAnnotationForRow = (rowKey: string): DeviationAnnotation | undefined => annotations?.find((a) => a.row_reference === rowKey)

    const handleRowMouseEnter = (rowKey: string, event: React.MouseEvent<HTMLElement>) => {
        setHoveredRow(rowKey)
        if (selectedRow === undefined) {
            setAnchorEl(event.currentTarget)
        }
    }

    const handleRowMouseLeave = () => {
        setHoveredRow(undefined)
        if (selectedRow === undefined) {
            setAnchorEl(null)
        }
    }

    return (
        <DeviationAnalysisContainer>
            {entries(transformedDeviationAnalysis).map(([sectionKey, section], index) => {
                const isSectionDefined = isKeyOf(sectionKey, copy.sectionLabels) && !isEmpty(section)
                return isSectionDefined ? (
                    <GroupedRowsContainer key={sectionKey}>
                        <LabelCell style={{ ...(index === 0 ? HEADER_CELL_STYLING : {}) }}>
                            <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                                {copy.sectionLabels[sectionKey]}
                            </Typography>
                        </LabelCell>

                        <ComparisonValueCell style={{ overflow: 'auto', ...(index === 0 ? HEADER_CELL_STYLING : {}) }}>
                            {index === 0 && (
                                <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                                    {isoDateStringToLocalFormat(comparisonValuation?.date, false, locale)}
                                </Typography>
                            )}
                        </ComparisonValueCell>

                        <BaseValueCell style={{ overflow: 'auto', ...(index === 0 ? HEADER_CELL_STYLING : {}) }}>
                            {index === 0 && (
                                <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                                    {isoDateStringToLocalFormat(baseValuation?.date, false, locale)}
                                </Typography>
                            )}
                        </BaseValueCell>

                        <DeviationValueCell style={{ ...(index === 0 ? HEADER_CELL_STYLING : {}) }}>
                            {index === 0 && (
                                <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                                    {copy.changeLabel}
                                </Typography>
                            )}
                        </DeviationValueCell>
                        <DeviationPercentageCell style={{ ...(index === 0 ? HEADER_CELL_STYLING : {}) }} />

                        {entries(section).map(([key, value], index, array) => {
                            const isLastRow = index === array.length - 1
                            const isRowDefined = value || isDeviationAnalysisPending
                            const annotationForRow = getAnnotationForRow(key)
                            const hideAnnotationBackground =
                                !!annotationForRow && filterOlderAnnotations && draftId !== annotationForRow.original_draft_uuid
                            const isRowHovered = hoveredRow === key
                            const isRowSelected = selectedRow === key
                            return isRowDefined ? (
                                <ClickAwayListener
                                    onClickAway={() => {
                                        if (isRowSelected) {
                                            setSelectedRow(undefined)
                                            setAnchorEl(null)
                                        }
                                    }}
                                    key={key}
                                >
                                    <Box
                                        sx={{
                                            borderTop:
                                                !annotationForRow || hideAnnotationBackground || isDeviationAnalysisPending
                                                    ? `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`
                                                    : undefined,
                                            borderBottom:
                                                (!annotationForRow || hideAnnotationBackground || isDeviationAnalysisPending) && isLastRow
                                                    ? `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`
                                                    : undefined,
                                            display: 'grid',
                                            gridColumn: '1 / -1',
                                            gridTemplateColumns: 'subgrid',
                                        }}
                                    >
                                        <RowContainer
                                            key={key}
                                            onClick={(event) => {
                                                if (hasNewerDraft && !annotationForRow) {
                                                    return
                                                }
                                                setSelectedRow(key)
                                                setAnchorEl(event.currentTarget)
                                            }}
                                            $hasAnnotation={!!annotationForRow && !isDeviationAnalysisPending}
                                            $hideAnnotationBackground={hideAnnotationBackground}
                                            $hasNewerDraft={hasNewerDraft}
                                            onMouseEnter={(event) => handleRowMouseEnter(key, event)}
                                            onMouseLeave={() => handleRowMouseLeave()}
                                            $selected={isRowSelected}
                                            $hovered={isRowHovered}
                                        >
                                            <LabelCell style={{ marginLeft: tokens.SizingSpacing100 }}>
                                                <DeviationLabel
                                                    label={isKeyOf(key, copy.rowLabels) ? getDeviationTableRowLabel(key, value) : key}
                                                    deviationChange={value?.deviation}
                                                    invertTrendColors={value?.isInvertedTrend}
                                                />
                                            </LabelCell>

                                            <ComparisonValueCell>
                                                {isDeviationAnalysisPending ? (
                                                    <LoadingSkeleton width="50%" />
                                                ) : (
                                                    <DeviationValue
                                                        deviationValue={value?.comparisonValuation}
                                                        locale={locale}
                                                        yearUnitLabels={yearUnitLabels}
                                                    />
                                                )}
                                            </ComparisonValueCell>

                                            <BaseValueCell
                                                $hasAnnotation={!!annotationForRow}
                                                $selected={isRowSelected}
                                                $hovered={isRowHovered}
                                                $hideAnnotationBackground={hideAnnotationBackground}
                                                $hasNewerDraft={hasNewerDraft}
                                            >
                                                {isDeviationAnalysisPending ? (
                                                    <LoadingSkeleton width="50%" />
                                                ) : (
                                                    <DeviationValue
                                                        deviationValue={value?.baseValue}
                                                        bold={true}
                                                        locale={locale}
                                                        yearUnitLabels={yearUnitLabels}
                                                    />
                                                )}
                                            </BaseValueCell>

                                            <DeviationValueCell>
                                                {isDeviationAnalysisPending ? (
                                                    <LoadingSkeleton width="50%" />
                                                ) : (
                                                    <DeviationChangeValue
                                                        deviationChange={value?.deviation}
                                                        percentagePointUnit={percentagePointUnit}
                                                        locale={locale}
                                                        yearUnitLabels={yearUnitLabels}
                                                    />
                                                )}
                                            </DeviationValueCell>

                                            <DeviationPercentageCell>
                                                {isDeviationAnalysisPending ? (
                                                    <LoadingSkeleton width="40%" />
                                                ) : (
                                                    <Box display="flex" alignItems="center" gap={tokens.SizingSpacing100}>
                                                        <DeviationChangePercentage
                                                            deviationChange={value?.deviation}
                                                            deviationPercentage={value?.deviationPercentage}
                                                            withoutChip
                                                            invertTrendColors={value?.isInvertedTrend}
                                                            percentagePointUnit={percentagePointUnit}
                                                            locale={locale}
                                                            yearUnitLabels={yearUnitLabels}
                                                        />
                                                        <AnnotationUserAvatar user={annotationForRow?.created_by_user} />
                                                    </Box>
                                                )}
                                            </DeviationPercentageCell>

                                            <Popper
                                                open={
                                                    isRowSelected ||
                                                    (!selectedRow && !!annotationForRow && hoveredRow === annotationForRow.row_reference)
                                                }
                                                anchorEl={anchorEl}
                                                placement="bottom"
                                                sx={{ zIndex: 100 }}
                                                modifiers={[
                                                    {
                                                        name: 'offset',
                                                        options: {
                                                            offset: [0, -150],
                                                        },
                                                    },
                                                ]}
                                            >
                                                {annotationForRow && (
                                                    <Box width="360px">
                                                        <TextBubble
                                                            type="popup"
                                                            annotation={annotationForRow}
                                                            addReply={addReply}
                                                            editAnnotation={editAnnotation}
                                                            editReply={editReply}
                                                            deleteAnnotation={deleteAnnotation}
                                                            deleteReply={deleteReply}
                                                            currentUser={currentUser}
                                                            isAddAnnotationPending={isAddAnnotationPending}
                                                            isDeleteAnnotationPending={isDeleteAnnotationPending}
                                                            isEditAnnotationPending={isEditAnnotationPending}
                                                            isImmediatelyVisible={isImmediatelyVisible}
                                                            showDraftVersion={hasMultipleDrafts}
                                                            showNotificationTooltip={showNotificationTooltip}
                                                            draftId={draftId}
                                                            hasNewerDraft={hasNewerDraft}
                                                            copy={textBubbleCopy}
                                                            userLinkCopy={userLinkCopy}
                                                            locale={locale}
                                                        />
                                                    </Box>
                                                )}
                                            </Popper>
                                            <Popper
                                                open={isRowSelected && !annotationForRow && !!anchorEl}
                                                anchorEl={anchorEl}
                                                placement="top"
                                                sx={{ zIndex: 100 }}
                                            >
                                                <Box width="360px">
                                                    <TextBubbleCreation
                                                        type="popup"
                                                        onCancel={() => setTimeout(() => setSelectedRow(undefined), 0)}
                                                        currentUser={currentUser}
                                                        onSubmit={(text) => {
                                                            addAnnotation({
                                                                text,
                                                                row_reference: key,
                                                                annotation_type: 'deviation',
                                                            })
                                                        }}
                                                        isPending={isAddAnnotationPending}
                                                        isImmediatelyVisible={isImmediatelyVisible}
                                                        showNotificationTooltip={showNotificationTooltip}
                                                        copy={textBubbleCopy}
                                                        userLinkCopy={userLinkCopy}
                                                    />
                                                </Box>
                                            </Popper>
                                        </RowContainer>
                                    </Box>
                                </ClickAwayListener>
                            ) : null
                        })}
                    </GroupedRowsContainer>
                ) : null
            })}
        </DeviationAnalysisContainer>
    )
}

const DeviationAnalysisContainer = styled('div')(() => ({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 1fr) 1fr 1fr 1fr minmax(140px, 0.6fr)',
    height: 'fit-content',
}))

const GroupedRowsContainer = styled('div')(() => ({
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: 'subgrid',
    marginRight: tokens.SizingSpacing200,
}))

const RowContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $hasAnnotation: boolean; $selected?: boolean; $hovered?: boolean; $hideAnnotationBackground: boolean; $hasNewerDraft: boolean }>(
    ({ $hasAnnotation, $selected, $hovered, $hideAnnotationBackground, $hasNewerDraft }) => ({
        position: 'relative',
        backgroundColor:
            ($hasAnnotation && !$hideAnnotationBackground) || $selected
                ? $hovered
                    ? '#f0edcc'
                    : '#FDFBE6'
                : $hideAnnotationBackground || $hasNewerDraft
                  ? 'transparent'
                  : $hovered
                    ? '#FDFBE6'
                    : 'transparent',
        boxShadow: ($hasAnnotation && !$hideAnnotationBackground) || $selected ? `inset 0 0 0 ${tokens.SizingBorderM} #F2EF5A` : 'none',
        cursor: 'pointer',
        display: 'grid',
        gridColumn: '1 / -1',
        gridTemplateColumns: 'subgrid',
        borderRadius: tokens.SizingCornerS,
    })
)

const GRID_CELL_STYLING = {
    height: '34px',
    alignItems: 'center',
    display: 'flex',
}

const HEADER_CELL_STYLING = {
    borderTop: 'none',
}

const LabelCell = styled('div')({
    ...GRID_CELL_STYLING,
    paddingLeft: tokens.SizingSpacing100,
    paddingRight: tokens.SizingSpacing100,
    whiteSpace: 'nowrap',
})

const ComparisonValueCell = styled('div')({
    ...GRID_CELL_STYLING,
    paddingLeft: tokens.SizingSpacing100,
    paddingRight: tokens.SizingSpacing100,
    justifyContent: 'flex-end',
})

const BaseValueCell = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $hasAnnotation?: boolean; $selected?: boolean; $hovered?: boolean; $hideAnnotationBackground?: boolean; $hasNewerDraft?: boolean }>(
    ({ $hasAnnotation, $selected, $hovered, $hideAnnotationBackground, $hasNewerDraft }) => ({
        ...GRID_CELL_STYLING,
        paddingLeft: tokens.SizingSpacing100,
        paddingRight: tokens.SizingSpacing100,
        backgroundColor: $hasNewerDraft
            ? $hasAnnotation && !$hideAnnotationBackground
                ? 'transparent'
                : tokens.ColorBackgroundDark
            : ($hasAnnotation || $selected || $hovered) && !$hideAnnotationBackground
              ? 'transparent'
              : tokens.ColorBackgroundDark,
        justifyContent: 'flex-end',
    })
)

const DeviationValueCell = styled('div')({
    ...GRID_CELL_STYLING,
    paddingLeft: tokens.SizingSpacing100,
    paddingRight: tokens.SizingSpacing100,
    justifyContent: 'flex-end',
    textAlign: 'right',
})

const DeviationPercentageCell = styled('div')({
    ...GRID_CELL_STYLING,
    paddingLeft: tokens.SizingSpacing100,
    paddingRight: tokens.SizingSpacing100,
    justifyContent: 'flex-end',
})
